<script setup>
import { ref } from 'vue';

/**
 * Bottom-sliding modal panel with optional title and drag handle.
 *
 * @status recommended
 * @group navigation
 * @useWhen Presenting actions, forms, or contextual content in a panel that slides up from the bottom on mobile
 * @interfaceShape imperative
 * @see https://quasar.dev/vue-components/dialog Quasar q-dialog API
 */
defineOptions({ name: 'HrBottomSheet' });

defineProps({
  /** Whether the sheet is open. */
  modelValue: {
    type: Boolean,
    default: false,
  },
  /**
   * Title displayed in the sheet header.
   * @example Select option
   */
  title: {
    type: String,
    default: null,
  },
});

const emit = defineEmits({
  /** Emitted when the sheet open state changes. */
  'update:modelValue': null,
});

const qDialogRef = ref(null);

defineExpose({
  /** Opens the sheet. */
  show: () => qDialogRef.value?.show(),
  /** Closes the sheet. */
  hide: () => qDialogRef.value?.hide(),
});
</script>

<template>
  <q-dialog
    ref="qDialogRef"
    :model-value="modelValue"
    position="bottom"
    transition-show="slide-up"
    transition-hide="slide-down"
    v-bind="$attrs"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="hr-bottom-sheet">
      <div class="hr-bottom-sheet__handle-row">
        <div class="hr-bottom-sheet__handle" />
      </div>
      <!-- @slot header Custom header area; defaults to the title bar when `title` is set. -->
      <slot name="header">
        <div
          v-if="title"
          class="hr-bottom-sheet__title-row"
        >
          <span class="hr-title-large">{{ title }}</span>
        </div>
      </slot>
      <div class="hr-bottom-sheet__content">
        <!-- @slot Sheet content. -->
        <slot />
      </div>
    </div>
  </q-dialog>
</template>

<style lang="scss">

/*
 * Not scoped — Quasar teleports dialogs outside the component DOM,
 * so scoped styles cannot reach it.
 *
 * Structural rules (shape, handle) apply in all themes.
 * Color rules are guarded by [data-theme] and use M3 tokens.
 */

.hr-bottom-sheet {
  width: 100%;
  background: white; /* fallback for legacy (no theme) */

  /*
   * A COLUMN THAT RESPECTS THE DIALOG'S CAP. Quasar limits the height of its bottom-positioned
   * inner; without honouring that, the sheet grows past it and the overflow:hidden below clips
   * whatever does not fit, with nothing to scroll. The handle and title stay put and only the
   * content moves, which is the M3 behaviour and also what keeps the drag affordance reachable.
   */
  display: flex;
  flex-direction: column;
  max-height: 100%;
}

[data-theme] .hr-bottom-sheet {
  /* M3 bottom sheets sit at elevation 2, which maps to surface-container-high */
  background: var(--app-surface-container-high);
  color: var(--app-on-surface);
  // The sheet already wears the search field's default tone, which would leave it invisible.
  --hr-search-bar-container: var(--app-surface-container-highest);
  border-radius: var(--app-shape-corner-extra-large) var(--app-shape-corner-extra-large) 0 0;
  overflow: hidden;
}

.hr-bottom-sheet__handle-row {
  display: flex;
  flex: none;
  justify-content: center;
  padding: 12px 0 4px;
}

.hr-bottom-sheet__handle {
  width: 32px;
  height: 4px;
  /* M3 full = 9999px pill; fall back for legacy */
  border-radius: var(--app-shape-corner-full, 9999px);
  background-color: var(--app-on-surface-variant, rgba(0, 0, 0, 0.5));
  opacity: 0.4;
}

.hr-bottom-sheet__title-row {
  flex: none;
  padding: 16px 24px 12px;
}

[data-theme] .hr-bottom-sheet__title-row {
  color: var(--app-on-surface);
}

.hr-bottom-sheet__content {
  padding: var(--app-comp-bottom-sheet-content-padding);

  /* min-height:0 is what lets a flex child shrink below its content height. Without it the child
     keeps its full height, the container never overflows, and overflow-y never engages. */
  min-height: 0;
  overflow-y: auto;
}

</style>
